import * as THREE from 'three';
import { createToonGradientMap, toonMat } from './materials.js';
import type { FpsBots } from './FpsBots.js';

const MAX_HP = 100;
const MAX_SHIELD = 100;
const FOLLOW_MIN = 8;
const FOLLOW_MAX = 14;
const FOLLOW_SPEED = 4.2;
const CHASE_SPEED = 5.4;
const SIGHT_RANGE = 24;
const SHOOT_RANGE = 20;
const SHOOT_CD = 1.1;
const BOT_HIT_CHANCE = 0.22;
const SHOT_DAMAGE = 5;
const KNOCK_BLEEDOUT = 25;
const REVIVE_HP = 50;
const REVIVE_RADIUS = 3;
const CARRY_RADIUS = 3;
const CARRY_BACK_OFFSET = 0.72;
const RESPAWN_DELAY = 30;
const ENEMY_RETURN_FIRE_RANGE = 22;
/** Seconds to hold at reboot van before teammate respawns (duos queue). */
export const REBOOT_VAN_QUEUE_HOLD = 4;

export type TeammateUpdateResult = {
  tracerEvents: { origin: THREE.Vector3; end: THREE.Vector3 }[];
  eliminations: { botName: string; pos: { x: number; z: number } }[];
};

export type TeammateSquadStats = {
  name: string;
  hp: number;
  shield: number;
  knocked: boolean;
  dead: boolean;
  bleedout: number;
  respawnIn: number;
};

type TeammateState = 'follow' | 'shoot' | 'knocked' | 'dead';

/** Friendly AI squadmate for Duos — follows the player and fights enemy bots. */
export class FpsTeammate {
  private root!: THREE.Group;
  private body!: THREE.Group;
  private gun!: THREE.Group;
  private leftArm!: THREE.Mesh;
  private rightArm!: THREE.Mesh;
  private leftLeg!: THREE.Mesh;
  private rightLeg!: THREE.Mesh;
  private hpBar!: HTMLDivElement;
  private hpFill!: HTMLDivElement;
  private nameTag!: HTMLDivElement;
  private hitMesh!: THREE.Mesh;
  private hp = MAX_HP;
  private shield = 50;
  private state: TeammateState = 'follow';
  private shootCd = 0.8;
  private walkPhase = 0;
  private yaw = 0;
  private knockedBleedout = 0;
  private respawnTimer = 0;
  private carried = false;
  private readonly grad: THREE.DataTexture;
  private readonly hudLayer: HTMLDivElement;
  private rebootQueueHud!: HTMLDivElement;
  private rebootQueueFill!: HTMLDivElement;
  private rebootQueueLabel!: HTMLDivElement;
  readonly name: string;

  constructor(
    private sceneRoot: THREE.Group,
    private bots: FpsBots,
    spawnX: number,
    spawnZ: number,
    name = 'SquadMate',
  ) {
    this.name = name;
    this.grad = createToonGradientMap();
    this.hudLayer = document.createElement('div');
    this.hudLayer.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:30;overflow:hidden';
    document.body.appendChild(this.hudLayer);
    this.buildMesh(spawnX, spawnZ);
  }

  private buildMesh(x: number, z: number): void {
    const root = new THREE.Group();
    root.position.set(x, 0, z);
    this.sceneRoot.add(root);
    this.root = root;

    const body = new THREE.Group();
    root.add(body);
    this.body = body;

    const armorColor = 0x2a6a3a;
    const accentColor = 0x44ff88;
    const skin = new THREE.Color().setHSL(0.07, 0.55, 0.62);

    const torsoMat = toonMat(armorColor, this.grad, {
      emissive: 0x0a2818,
      emissiveIntensity: 0.22,
    });
    const accentMat = toonMat(accentColor, this.grad, {
      emissive: accentColor,
      emissiveIntensity: 0.35,
    });
    const skinMat = toonMat(skin.getHex(), this.grad);

    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.78, 0.95, 0.42), torsoMat);
    torso.position.y = 1.25;
    torso.castShadow = true;
    body.add(torso);

    const belt = new THREE.Mesh(new THREE.BoxGeometry(0.82, 0.12, 0.46), accentMat);
    belt.position.y = 0.84;
    body.add(belt);

    const head = new THREE.Mesh(new THREE.SphereGeometry(0.24, 14, 12), skinMat);
    head.position.y = 1.92;
    head.castShadow = true;
    body.add(head);

    const armGeo = new THREE.CapsuleGeometry(0.1, 0.55, 4, 8);
    const leftArm = new THREE.Mesh(armGeo, torsoMat);
    leftArm.position.set(-0.46, 1.42, 0);
    body.add(leftArm);
    this.leftArm = leftArm;
    const rightArm = new THREE.Mesh(armGeo, torsoMat);
    rightArm.position.set(0.46, 1.42, 0);
    body.add(rightArm);
    this.rightArm = rightArm;

    const legGeo = new THREE.CapsuleGeometry(0.13, 0.6, 4, 8);
    const legMat = toonMat(0x1a4a28, this.grad);
    const leftLeg = new THREE.Mesh(legGeo, legMat);
    leftLeg.position.set(-0.2, 0.5, 0);
    body.add(leftLeg);
    this.leftLeg = leftLeg;
    const rightLeg = new THREE.Mesh(legGeo, legMat);
    rightLeg.position.set(0.2, 0.5, 0);
    body.add(rightLeg);
    this.rightLeg = rightLeg;

    const gun = new THREE.Group();
    const gunBody = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.12, 0.5),
      new THREE.MeshStandardMaterial({ color: 0x3a3a45, metalness: 0.55, roughness: 0.4 }),
    );
    gun.add(gunBody);
    gun.position.set(0.4, 1.42, 0.35);
    body.add(gun);
    this.gun = gun;

    this.nameTag = document.createElement('div');
    this.nameTag.style.cssText =
      'position:absolute;transform:translate(-50%,-100%);padding:2px 7px;border-radius:4px;background:rgba(8,12,20,0.75);color:#36e36b;font:700 10px system-ui,sans-serif;text-shadow:0 1px 3px #000;pointer-events:none;white-space:nowrap;border:1px solid rgba(54,227,107,0.55)';
    this.nameTag.textContent = this.name;
    this.hudLayer.appendChild(this.nameTag);

    this.hpBar = document.createElement('div');
    this.hpBar.style.cssText =
      'position:absolute;transform:translate(-50%,-100%);width:48px;height:5px;border-radius:3px;background:rgba(0,0,0,0.55);border:1px solid rgba(54,227,107,0.35);overflow:hidden;pointer-events:none';
    this.hpFill = document.createElement('div');
    this.hpFill.style.cssText = 'height:100%;width:100%;background:linear-gradient(90deg,#36e36b,#9bff6a)';
    this.hpBar.appendChild(this.hpFill);
    this.hudLayer.appendChild(this.hpBar);

    this.rebootQueueHud = document.createElement('div');
    this.rebootQueueHud.style.cssText =
      'position:absolute;transform:translate(-50%,-100%);display:none;min-width:108px;padding:5px 8px;border-radius:6px;' +
      'background:rgba(12,28,56,0.88);border:1px solid rgba(102,204,255,0.65);pointer-events:none;text-align:center';
    this.rebootQueueLabel = document.createElement('div');
    this.rebootQueueLabel.style.cssText =
      'font:800 9px system-ui;color:#9ed8ff;letter-spacing:0.05em;margin-bottom:4px';
    this.rebootQueueLabel.textContent = 'REBOOT QUEUE';
    const rebootBar = document.createElement('div');
    rebootBar.style.cssText =
      'height:4px;border-radius:2px;background:rgba(0,0,0,0.5);overflow:hidden';
    this.rebootQueueFill = document.createElement('div');
    this.rebootQueueFill.style.cssText =
      'height:100%;width:0%;background:linear-gradient(90deg,#4db8ff,#9ee8ff);transition:width 0.05s linear';
    rebootBar.appendChild(this.rebootQueueFill);
    this.rebootQueueHud.append(this.rebootQueueLabel, rebootBar);
    this.hudLayer.appendChild(this.rebootQueueHud);

    this.hitMesh = new THREE.Mesh(
      new THREE.BoxGeometry(0.9, 1.95, 0.55),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    this.hitMesh.position.y = 1.0;
    this.hitMesh.userData.teammate = true;
    body.add(this.hitMesh);
  }

  isAlive(): boolean {
    return this.state !== 'dead';
  }

  isShooting(): boolean {
    return this.state === 'shoot';
  }

  isKnocked(): boolean {
    return this.state === 'knocked';
  }

  isCarried(): boolean {
    return this.carried;
  }

  isCarryable(): boolean {
    return this.state === 'knocked' && !this.carried;
  }

  isDead(): boolean {
    return this.state === 'dead';
  }

  needsReboot(): boolean {
    return this.state === 'dead' || this.state === 'knocked';
  }

  rebootFromVan(x: number, z: number): boolean {
    if (this.state === 'dead') {
      this.respawnTimer = 0;
      this.root.position.set(x, 0, z);
      this.hp = MAX_HP;
      this.shield = 50;
      this.state = 'follow';
      this.knockedBleedout = 0;
      this.shootCd = 0.6;
      this.resetStandingPose();
      this.gun.visible = true;
      this.root.visible = true;
      this.nameTag.textContent = this.name;
      this.nameTag.style.color = '#36e36b';
      this.nameTag.style.borderColor = 'rgba(54,227,107,0.55)';
      this.hpFill.style.background = 'linear-gradient(90deg,#36e36b,#9bff6a)';
      this.hpFill.style.width = '100%';
      return true;
    }
    if (this.state === 'knocked') {
      this.revive();
      this.shield = MAX_SHIELD;
      this.root.position.set(x, 0, z);
      return true;
    }
    return false;
  }

  isWithinReviveRange(px: number, pz: number, radius = REVIVE_RADIUS): boolean {
    if (this.state !== 'knocked' || this.carried) return false;
    const dx = px - this.root.position.x;
    const dz = pz - this.root.position.z;
    return dx * dx + dz * dz <= radius * radius;
  }

  /** While carried: mesh follows carrier back offset, bleedout paused, world HUD hidden. */
  setCarried(on: boolean, carrierPos?: THREE.Vector3, carrierYaw?: number): void {
    if (on) {
      if (this.state !== 'knocked') return;
      this.carried = true;
      this.nameTag.style.display = 'none';
      this.hpBar.style.display = 'none';
      if (carrierPos != null) this.syncCarriedPosition(carrierPos, carrierYaw ?? this.yaw);
      return;
    }
    this.carried = false;
    if (carrierPos != null) {
      this.root.position.set(carrierPos.x, 0, carrierPos.z);
    }
    this.root.visible = true;
    this.applyKnockedPose();
  }

  private syncCarriedPosition(carrierPos: THREE.Vector3, carrierYaw: number): void {
    const backX = carrierPos.x - Math.sin(carrierYaw) * CARRY_BACK_OFFSET;
    const backZ = carrierPos.z - Math.cos(carrierYaw) * CARRY_BACK_OFFSET;
    this.root.position.set(backX, 0.42, backZ);
    this.yaw = carrierYaw;
    this.body.rotation.y = carrierYaw;
    this.body.rotation.z = 0;
  }

  revive(): void {
    if (this.state !== 'knocked') return;
    this.carried = false;
    this.state = 'follow';
    this.hp = REVIVE_HP;
    this.shield = 0;
    this.knockedBleedout = 0;
    this.shootCd = 0.5;
    this.resetStandingPose();
    this.gun.visible = true;
    this.nameTag.textContent = this.name;
    this.nameTag.style.color = '#36e36b';
    this.nameTag.style.borderColor = 'rgba(54,227,107,0.55)';
    this.hpFill.style.background = 'linear-gradient(90deg,#36e36b,#9bff6a)';
    this.hpFill.style.width = `${(this.hp / MAX_HP) * 100}%`;
  }

  /** Van reboot queue HUD — progress 0–1, secondsLeft counts down. */
  setRebootQueueHud(active: boolean, progress = 0, secondsLeft = REBOOT_VAN_QUEUE_HOLD): void {
    if (!active) {
      this.rebootQueueHud.style.display = 'none';
      this.rebootQueueFill.style.width = '0%';
      return;
    }
    this.rebootQueueHud.style.display = 'block';
    this.rebootQueueLabel.textContent = `REBOOT ${Math.max(0, secondsLeft).toFixed(1)}s`;
    this.rebootQueueFill.style.width = `${Math.min(100, Math.max(0, progress * 100))}%`;
  }

  getSquadStats(): TeammateSquadStats {
    return {
      name: this.name,
      hp: this.state === 'knocked' ? 0 : this.hp,
      shield: this.shield,
      knocked: this.state === 'knocked',
      dead: this.state === 'dead',
      bleedout: this.knockedBleedout,
      respawnIn: this.respawnTimer,
    };
  }

  getPosition(out: THREE.Vector3): THREE.Vector3 {
    return out.set(this.root.position.x, this.root.position.y + 1.05, this.root.position.z);
  }

  /** Enemy bots may chip the teammate when nearby and shooting. */
  update(dt: number, playerPos: THREE.Vector3, camera: THREE.PerspectiveCamera): TeammateUpdateResult {
    const tracerEvents: TeammateUpdateResult['tracerEvents'] = [];
    const eliminations: TeammateUpdateResult['eliminations'] = [];

    if (this.state === 'dead') {
      this.respawnTimer -= dt;
      this.nameTag.style.display = 'none';
      this.hpBar.style.display = 'none';
      this.root.visible = false;
      if (this.respawnTimer <= 0) {
        this.respawnNear(playerPos);
      }
      return { tracerEvents, eliminations };
    }

    if (this.state === 'knocked') {
      if (this.carried) {
        this.root.visible = true;
        this.nameTag.style.display = 'none';
        this.hpBar.style.display = 'none';
        return { tracerEvents, eliminations };
      }
      this.root.visible = true;
      this.knockedBleedout -= dt;
      this.applyEnemyFireWhileKnocked(dt, playerPos);
      this.updateKnockedMotion(dt, playerPos);
      this.body.rotation.y = this.yaw;
      if (this.knockedBleedout <= 0) {
        this.eliminate();
        return { tracerEvents, eliminations };
      }
      this.updateHud(camera);
      return { tracerEvents, eliminations };
    }

    this.root.visible = true;
    this.applyEnemyFire(dt, playerPos);

    const tx = this.root.position.x;
    const tz = this.root.position.z;
    const pdx = playerPos.x - tx;
    const pdz = playerPos.z - tz;
    const distToPlayer = Math.hypot(pdx, pdz);

    const target = this.findNearestEnemy();
    if (target && target.dist < SIGHT_RANGE) {
      this.state = target.dist < SHOOT_RANGE ? 'shoot' : 'follow';
    } else {
      this.state = 'follow';
    }

    let moveTx = playerPos.x;
    let moveTz = playerPos.z;
    let speed = FOLLOW_SPEED;

    if (this.state === 'shoot' && target) {
      moveTx = target.x;
      moveTz = target.z;
      speed = distToPlayer > FOLLOW_MAX ? CHASE_SPEED : FOLLOW_SPEED * 0.55;
    } else if (distToPlayer > FOLLOW_MAX) {
      speed = CHASE_SPEED;
    } else if (distToPlayer < FOLLOW_MIN) {
      if (distToPlayer > 0.05) {
        moveTx = tx - (pdx / distToPlayer) * (FOLLOW_MIN - distToPlayer + 0.5);
        moveTz = tz - (pdz / distToPlayer) * (FOLLOW_MIN - distToPlayer + 0.5);
      }
      speed = FOLLOW_SPEED * 0.65;
    } else {
      speed = 0;
    }

    const mdx = moveTx - tx;
    const mdz = moveTz - tz;
    const moveDist = Math.hypot(mdx, mdz);
    if (speed > 0 && moveDist > 0.35) {
      const nx = mdx / moveDist;
      const nz = mdz / moveDist;
      this.root.position.x += nx * speed * dt;
      this.root.position.z += nz * speed * dt;
      this.walkPhase += dt * speed * 2.2;
    }

    const faceX = target && this.state === 'shoot' ? target.x : playerPos.x;
    const faceZ = target && this.state === 'shoot' ? target.z : playerPos.z;
    const fdx = faceX - tx;
    const fdz = faceZ - tz;
    const desiredYaw = Math.atan2(fdx, fdz);
    const dy = ((desiredYaw - this.yaw + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
    this.yaw += dy * Math.min(1, 5 * dt);
    this.body.rotation.y = this.yaw;

    const swing = speed > 0.1 ? Math.sin(this.walkPhase) * 0.55 : 0;
    this.leftArm.rotation.x = swing;
    this.rightArm.rotation.x = -swing;

    this.shootCd -= dt;
    if (this.state === 'shoot' && target && this.shootCd <= 0) {
      this.shootCd = SHOOT_CD + Math.random() * 0.35;
      const origin = new THREE.Vector3(
        tx + Math.sin(this.yaw) * 0.4,
        this.root.position.y + 1.4,
        tz + Math.cos(this.yaw) * 0.4,
      );
      const aim = new THREE.Vector3(target.x, target.y + 1.35, target.z);
      const end = aim.clone();
      tracerEvents.push({ origin: origin.clone(), end });

      const buildHit = this.bots.findBotBuildHit(origin, aim);
      if (buildHit) {
        this.bots.damageBotBuildFromHit(buildHit, SHOT_DAMAGE);
      } else if (Math.random() < BOT_HIT_CHANCE) {
        const hit = this.bots.applyExternalDamage(target.id, SHOT_DAMAGE, this.name, origin);
        if (hit?.killed) {
          eliminations.push({
            botName: hit.name,
            pos: { x: hit.pos.x, z: hit.pos.z },
          });
        }
      }
      this.flashMuzzle();
    }

    this.updateHud(camera);
    return { tracerEvents, eliminations };
  }

  private findNearestEnemy(): { id: number; x: number; y: number; z: number; dist: number } | null {
    const tx = this.root.position.x;
    const tz = this.root.position.z;
    let best: { id: number; x: number; y: number; z: number; dist: number } | null = null;
    for (const bot of this.bots.getLivingBots()) {
      const dist = Math.hypot(bot.x - tx, bot.z - tz);
      if (!best || dist < best.dist) {
        best = { id: bot.id, x: bot.x, y: bot.y, z: bot.z, dist };
      }
    }
    return best;
  }

  private applyDamage(raw: number): void {
    let remain = raw;
    const fromShield = Math.min(this.shield, remain);
    this.shield -= fromShield;
    remain -= fromShield;
    if (remain > 0) this.hp -= remain;
    if (this.hp <= 0) this.enterKnocked();
  }

  private applyEnemyFire(dt: number, playerPos: THREE.Vector3): void {
    const tx = this.root.position.x;
    const tz = this.root.position.z;
    for (const bot of this.bots.getLivingBots()) {
      const dist = Math.hypot(bot.x - tx, bot.z - tz);
      if (dist > ENEMY_RETURN_FIRE_RANGE) continue;
      const toTeammate = dist < distTo(playerPos, bot.x, bot.z) ? 0.35 : 0.12;
      if (Math.random() > toTeammate * dt * 0.45) continue;
      this.applyDamage(4 + Math.random() * 3);
      if (this.state === 'knocked' || this.state === 'dead') return;
    }
  }

  private applyEnemyFireWhileKnocked(dt: number, playerPos: THREE.Vector3): void {
    const tx = this.root.position.x;
    const tz = this.root.position.z;
    for (const bot of this.bots.getLivingBots()) {
      const dist = Math.hypot(bot.x - tx, bot.z - tz);
      if (dist > ENEMY_RETURN_FIRE_RANGE) continue;
      const toTeammate = dist < distTo(playerPos, bot.x, bot.z) ? 0.35 : 0.12;
      if (Math.random() > toTeammate * dt * 0.45) continue;
      this.knockedBleedout = Math.max(0, this.knockedBleedout - 2.8);
      if (this.knockedBleedout <= 0) {
        this.eliminate();
        return;
      }
    }
  }

  private enterKnocked(): void {
    if (this.state === 'knocked' || this.state === 'dead') return;
    this.state = 'knocked';
    this.hp = 0;
    this.shield = 0;
    this.knockedBleedout = KNOCK_BLEEDOUT;
    this.shootCd = 0.5;
    this.applyKnockedPose();
    this.nameTag.textContent = `${this.name} · KNOCKED`;
    this.nameTag.style.color = '#ff9a3c';
    this.nameTag.style.borderColor = 'rgba(255,154,60,0.55)';
    this.hpFill.style.background = 'linear-gradient(90deg,#ff9a3c,#ffb86a)';
    this.hpFill.style.width = '100%';
  }

  private applyKnockedPose(): void {
    this.body.rotation.x = Math.PI / 2;
    this.body.position.y = 0.28;
    this.gun.visible = false;
    this.leftArm.rotation.x = 0.15;
    this.rightArm.rotation.x = -0.1;
    this.leftLeg.rotation.x = 0.05;
    this.rightLeg.rotation.x = -0.05;
  }

  private resetStandingPose(): void {
    this.body.rotation.x = 0;
    this.body.position.y = 0;
    this.leftArm.rotation.x = 0;
    this.rightArm.rotation.x = 0;
    this.leftLeg.rotation.x = 0;
    this.rightLeg.rotation.x = 0;
  }

  private updateKnockedMotion(dt: number, playerPos: THREE.Vector3): void {
    const tx = this.root.position.x;
    const tz = this.root.position.z;
    const pdx = playerPos.x - tx;
    const pdz = playerPos.z - tz;
    const dist = Math.hypot(pdx, pdz);
    if (dist > 2.2 && dist < 12) {
      const crawl = 1.35 * dt;
      this.root.position.x += (pdx / dist) * crawl;
      this.root.position.z += (pdz / dist) * crawl;
      this.yaw = Math.atan2(pdx, pdz);
    }
    this.body.rotation.z = Math.sin(performance.now() * 0.0018) * 0.03;
  }

  private eliminate(): void {
    this.state = 'dead';
    this.carried = false;
    this.hp = 0;
    this.shield = 0;
    this.knockedBleedout = 0;
    this.respawnTimer = RESPAWN_DELAY;
    this.resetStandingPose();
    this.hpBar.style.display = 'none';
    this.nameTag.textContent = `${this.name} · ELIMINATED`;
    this.nameTag.style.color = '#ff7070';
  }

  private respawnNear(playerPos: THREE.Vector3): void {
    const ang = Math.random() * Math.PI * 2;
    this.root.position.set(
      playerPos.x + Math.cos(ang) * 3.5,
      0,
      playerPos.z + Math.sin(ang) * 3.5,
    );
    this.hp = MAX_HP;
    this.shield = 50;
    this.state = 'follow';
    this.shootCd = 0.6;
    this.knockedBleedout = 0;
    this.resetStandingPose();
    this.gun.visible = true;
    this.nameTag.textContent = this.name;
    this.nameTag.style.color = '#36e36b';
    this.nameTag.style.borderColor = 'rgba(54,227,107,0.55)';
    this.hpFill.style.background = 'linear-gradient(90deg,#36e36b,#9bff6a)';
    this.hpFill.style.width = '100%';
  }

  private flashMuzzle(): void {
    const flash = new THREE.PointLight(0x66ffaa, 4, 6);
    this.gun.add(flash);
    flash.position.set(0, 0, 0.45);
    setTimeout(() => {
      this.gun.remove(flash);
      flash.dispose();
    }, 60);
  }

  private updateHud(camera: THREE.PerspectiveCamera): void {
    const worldPos = new THREE.Vector3(
      this.root.position.x,
      this.root.position.y + 2.5,
      this.root.position.z,
    );
    const projected = worldPos.clone().project(camera);
    if (projected.z >= 1) {
      this.hpBar.style.display = 'none';
      this.nameTag.style.display = 'none';
      if (this.rebootQueueHud.style.display !== 'block') {
        this.rebootQueueHud.style.display = 'none';
      }
      return;
    }
    const x = (projected.x * 0.5 + 0.5) * window.innerWidth;
    const y = (1 - (projected.y * 0.5 + 0.5)) * window.innerHeight;
    this.hpBar.style.left = `${x}px`;
    this.hpBar.style.top = `${y}px`;
    this.nameTag.style.left = `${x}px`;
    this.nameTag.style.top = `${y - 8}px`;
    this.rebootQueueHud.style.left = `${x}px`;
    this.rebootQueueHud.style.top = `${y + 14}px`;
    this.hpBar.style.display = 'block';
    this.nameTag.style.display = 'block';
    if (this.state === 'knocked') {
      this.hpFill.style.width = `${Math.max(0, (this.knockedBleedout / KNOCK_BLEEDOUT) * 100)}%`;
      return;
    }
    this.hpFill.style.width = `${Math.max(0, (this.hp / MAX_HP) * 100)}%`;
  }

  dispose(): void {
    this.root.removeFromParent();
    this.hpBar.remove();
    this.nameTag.remove();
    this.rebootQueueHud.remove();
    this.hudLayer.remove();
  }
}

function distTo(pos: THREE.Vector3, x: number, z: number): number {
  return Math.hypot(pos.x - x, pos.z - z);
}

export { REVIVE_RADIUS, CARRY_RADIUS, KNOCK_BLEEDOUT, RESPAWN_DELAY };
